import { useState, useCallback, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { Map, Search, AlertCircle, Loader2, Thermometer } from 'lucide-react'
import ProtectedRoute from '../components/auth/ProtectedRoute'
import { AppHeader } from '../components'
import ThermalMap, { ThermalLocation, TaskTurnpoint } from '../components/events/ThermalMap'
import { useActiveCompetition } from '../hooks/useActiveCompetition'
import { parseXctsk } from '../services/albatross'

const _apiBase = (import.meta.env.VITE_API_URL as string | undefined) ||
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
  'http://localhost:3001'
const ALBATROSS_PROXY = `${_apiBase}/albatross`

function getAuthHeaders(): Record<string, string> {
  try {
    const stored = localStorage.getItem('fwo_auth_session')
    if (!stored) return {}
    const session = JSON.parse(stored) as Record<string, unknown>
    const token = typeof session.idToken === 'string' ? session.idToken : null
    return token ? { Authorization: `Bearer ${token}` } : {}
  } catch {
    return {}
  }
}

type ViewMode = 'task' | 'competition'

interface AlbatrossTask {
  id: string
  name: string
  date: string
  xctsk?: string
}

// Parse a ThermalAtlas response (Albatross API). Canonical shape per
// docs/albatross-handoff.md → upstream docs/API.md (ThermalAtlas / ThermalCluster).
function parseThermals(data: unknown): ThermalLocation[] {
  if (!data || typeof data !== 'object') return []
  const root = data as Record<string, unknown>
  const atlas = root.data && typeof root.data === 'object' ? root.data as Record<string, unknown> : root
  const clusters = Array.isArray(atlas.clusters) ? atlas.clusters : []

  return clusters
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map((c, idx) => ({
      id: String(c.id ?? idx),
      lat: Number(c.centerLat ?? 0),
      lon: Number(c.centerLon ?? 0),
      avgClimbRate: Number(c.avgClimb ?? 0),
      maxClimbRate: c.maxClimb !== undefined ? Number(c.maxClimb) : undefined,
      count: c.thermalCount !== undefined ? Number(c.thermalCount) : undefined,
      altitude: c.maxAltitude !== undefined ? Number(c.maxAltitude) : undefined,
    }))
    .filter((t) => t.lat !== 0 || t.lon !== 0)
}

export default function ThermalMapPage() {
  const { competition, albatrossCompId: activeCompId } = useActiveCompetition()
  const [viewMode, setViewMode] = useState<ViewMode>('task')
  const [selectedTaskId, setSelectedTaskId] = useState('')
  const [tasks, setTasks] = useState<AlbatrossTask[]>([])
  const [tasksLoading, setTasksLoading] = useState(false)

  const [thermals, setThermals] = useState<ThermalLocation[]>([])
  const [taskTurnpoints, setTaskTurnpoints] = useState<TaskTurnpoint[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadedLabel, setLoadedLabel] = useState<string | null>(null)

  // Fetch tasks from Albatross when in task view and we have a competition ID
  useEffect(() => {
    if (viewMode !== 'task' || !activeCompId) return
    let cancelled = false
    setTasksLoading(true)
    fetch(`${ALBATROSS_PROXY}/competitions/${encodeURIComponent(activeCompId)}/tasks`, { headers: getAuthHeaders() })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`${r.status}`)))
      .then((json: { success: boolean; data: AlbatrossTask[] }) => {
        if (cancelled) return
        const sorted = (json.data || []).slice().sort((a, b) => a.date.localeCompare(b.date))
        setTasks(sorted)
      })
      .catch(() => { if (!cancelled) setTasks([]) })
      .finally(() => { if (!cancelled) setTasksLoading(false) })
    return () => { cancelled = true }
  }, [viewMode, activeCompId])

  const fetchThermals = useCallback(async () => {
    let id: string
    let label: string

    if (viewMode === 'task') {
      if (!selectedTaskId) {
        setError('Please select a task.')
        return
      }
      id = selectedTaskId
      const task = tasks.find((t) => t.id === id)
      label = task ? `${task.name} (${task.date})` : id
    } else {
      if (!activeCompId) {
        setError('No competition linked.')
        return
      }
      id = activeCompId
      label = competition?.name || id
    }

    setLoading(true)
    setError(null)
    setThermals([])
    setLoadedLabel(null)

    // For task view, derive turnpoints from the task's xctsk JSON for map overlay
    if (viewMode === 'task') {
      const task = tasks.find((t) => t.id === id)
      const parsed = parseXctsk(task?.xctsk)
      setTaskTurnpoints(
        parsed
          ? parsed.turnpoints.map((tp) => ({
              lat: tp.waypoint.lat,
              lon: tp.waypoint.lon,
              radius: tp.radius,
              name: tp.waypoint.name,
              description: tp.waypoint.description,
              type: tp.type,
            }))
          : []
      )
    } else {
      setTaskTurnpoints([])
    }

    try {
      const url =
        viewMode === 'task'
          ? `${ALBATROSS_PROXY}/v1/tasks/${encodeURIComponent(id)}/thermal-atlas`
          : `${ALBATROSS_PROXY}/v1/competitions/${encodeURIComponent(id)}/thermal-atlas`

      const response = await fetch(url, { headers: getAuthHeaders() })

      if (!response.ok) {
        const text = await response.text().catch(() => '')
        throw new Error(
          `API error ${response.status}${text ? `: ${text.slice(0, 120)}` : ''}`
        )
      }

      const data: unknown = await response.json()
      const parsed = parseThermals(data)

      setThermals(parsed)
      setLoadedLabel(label)
      if (parsed.length === 0) {
        setError('No thermal data found for this selection. The atlas may be empty or not yet generated.')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch thermal data.')
    } finally {
      setLoading(false)
    }
  }, [selectedTaskId, viewMode, activeCompId, competition, tasks])

  return (
    <ProtectedRoute>
      <Helmet>
        <title>Thermal Map - FWO Pilot Portal</title>
        <meta name="description" content="Thermal intelligence map from Albatross flight data" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <AppHeader activePage="thermal" />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-1">
              <Thermometer className="h-7 w-7 text-fwo-blue" />
              <h1 className="text-2xl font-bold text-gray-900">Thermal Intelligence Map</h1>
            </div>
            <p className="text-gray-500 text-sm ml-10">
              Visualise thermal activity from Albatross flight data. Circles show thermal locations
              coloured by average climb rate.
            </p>
          </div>

          {/* Controls */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-6">
            {/* View Mode Toggle */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-sm font-medium text-gray-700">View:</span>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                <button
                  onClick={() => { setViewMode('task'); setThermals([]); setError(null); setLoadedLabel(null) }}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'task'
                      ? 'bg-fwo-blue text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Per Task
                </button>
                <button
                  onClick={() => { setViewMode('competition'); setThermals([]); setError(null); setLoadedLabel(null) }}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-l border-gray-300 ${
                    viewMode === 'competition'
                      ? 'bg-fwo-blue text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Competition-Wide
                </button>
              </div>
            </div>

            {/* Selection */}
            <div className="flex gap-3">
              <div className="flex-1">
                {viewMode === 'task' ? (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Task</label>
                    <select
                      value={selectedTaskId}
                      onChange={(e) => setSelectedTaskId(e.target.value)}
                      disabled={tasksLoading || tasks.length === 0}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fwo-blue focus:border-transparent text-sm bg-white"
                    >
                      <option value="">
                        {tasksLoading ? 'Loading tasks...' : tasks.length === 0 ? 'No tasks available' : 'Select a task'}
                      </option>
                      {tasks.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} — {t.date}
                        </option>
                      ))}
                    </select>
                  </>
                ) : (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Competition</label>
                    <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700">
                      {competition?.name || activeCompId || 'No competition linked'}
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-end">
                <button
                  onClick={fetchThermals}
                  disabled={loading || (viewMode === 'task' && !selectedTaskId) || (viewMode === 'competition' && !activeCompId)}
                  className="flex items-center gap-2 px-4 py-2 bg-fwo-blue text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                  {loading ? 'Loading...' : 'Load Map'}
                </button>
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Map */}
          {(thermals.length > 0 || taskTurnpoints.length > 0) && loadedLabel && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <Map className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">
                    {viewMode === 'task' ? 'Task' : 'Competition'}: {loadedLabel}
                  </span>
                </div>
                <span className="text-xs text-gray-500">
                  {thermals.length} thermal{thermals.length !== 1 ? 's' : ''}
                  {taskTurnpoints.length > 0 ? ` · ${taskTurnpoints.length} turnpoint${taskTurnpoints.length !== 1 ? 's' : ''}` : ''}
                  {thermals.length > 0 ? ' · click a circle for details' : ''}
                </span>
              </div>
              <div className="p-4">
                <ThermalMap thermals={thermals} turnpoints={taskTurnpoints} />
              </div>
            </div>
          )}

          {/* Empty state */}
          {thermals.length === 0 && taskTurnpoints.length === 0 && !error && !loading && (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <Map className="h-16 w-16 mb-4 opacity-30" />
              <p className="text-base">
                {viewMode === 'task' ? 'Select a task above' : 'Click Load Map'} to view thermal data
              </p>
            </div>
          )}
        </main>
      </div>
    </ProtectedRoute>
  )
}
